import React, { useState } from "react";
import {
  Check,
  Minus,
  ChevronDown,
  ChevronUp,
  Info,
  CheckSquare,
  Square,
} from "lucide-react";
import formatPrice0 from "../../../utility/formatPrice0";
import { LifecyclePhase } from "../../../store/types/settings";

interface LifecyclePhasesSectionProps {
  phases: LifecyclePhase[];
  selectedPhaseIds: number[];
  onTogglePhase: (id: number) => void;
  loading?: boolean;
}

const LifecyclePhasesSection: React.FC<LifecyclePhasesSectionProps> = ({
  phases,
  selectedPhaseIds,
  onTogglePhase,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section id="lifecycle-phases" className="pb-10 px-2 lg:px-3 mt-4">
      <div className="max-w-[950px] mx-auto">
        <div className="bg-white rounded-[15px] shadow-sm border border-primary overflow-hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors rounded-[15px]"
          >
            <h3 className="text-[15px] md:text-[17px] font-medium text-[#212121]">
              Employee Lifecycle Phases
            </h3>
            {isOpen ? (
              <ChevronUp className="w-6 h-6 text-gray-500" />
            ) : (
              <ChevronDown className="w-6 h-6 text-gray-500" />
            )}
          </button>

          {isOpen && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-3 w-[50px] text-center">
                      <div
                        className="flex items-center justify-center cursor-pointer"
                        onClick={() => {
                          const allSelected = phases.every(
                            (p) =>
                              selectedPhaseIds.includes(p.id) || p.isMandatory,
                          );
                          phases.forEach((p) => {
                            if (!p.isMandatory) {
                              if (
                                allSelected &&
                                selectedPhaseIds.includes(p.id)
                              ) {
                                onTogglePhase(p.id);
                              } else if (
                                !allSelected &&
                                !selectedPhaseIds.includes(p.id)
                              ) {
                                onTogglePhase(p.id);
                              }
                            }
                          });
                        }}
                      >
                        {phases.length > 0 &&
                        phases
                          .filter((p) => !p.isMandatory)
                          .every((p) => selectedPhaseIds.includes(p.id)) ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="text-left px-6 py-3 text-[14px] font-medium text-[#212121] w-[40%]">
                      Phase
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[20%]">
                      Setup Fee
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[20%]">
                      Monthly Subscription
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[20%]">
                      Year 1 Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading
                    ? Array.from({ length: 3 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td
                            colSpan={5}
                            className="px-6 py-4 h-12 bg-gray-50/50"
                          ></td>
                        </tr>
                      ))
                    : phases.map((phase) => {
                        const isSelected =
                          selectedPhaseIds.includes(phase.id) ||
                          phase.isMandatory;
                        return (
                          <tr
                            key={phase.id}
                            className={`hover:bg-gray-50 transition-colors ${
                              isSelected ? "bg-blue-50/50" : ""
                            }`}
                            onClick={() =>
                              !phase.isMandatory && onTogglePhase(phase.id)
                            }
                          >
                            <td className="px-3 py-3 text-center cursor-pointer">
                              <div className="flex items-center justify-center">
                                {phase.isMandatory ? (
                                  <CheckSquare className="w-5 h-5 text-gray-400 cursor-not-allowed" />
                                ) : isSelected ? (
                                  <CheckSquare className="w-5 h-5 text-blue-600" />
                                ) : (
                                  <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-3 text-[14px] text-[#616161] ">
                              <div className="flex flex-col text-left items-start">
                                <span className="font-semibold text-gray-900">
                                  {phase.name}
                                </span>
                                <span className="text-xs text-gray-500 mt-1">
                                  {phase.description}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-3 text-center text-[14px] font-medium">
                              {/* * 100, "USD" */}
                              {formatPrice0(phase.setupFee, "NGN")}
                            </td>
                            <td className="px-6 py-3 text-center text-[14px] font-medium">
                              {formatPrice0(phase.monthlySubscription, "NGN")}
                            </td>
                            <td className="px-6 py-3 text-center text-[14px] font-medium text-emerald-600">
                              {formatPrice0(phase.year1Total, "NGN")}
                            </td>
                          </tr>
                        );
                      })}
                  {phases.length === 0 && !loading && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-8 text-center text-gray-500"
                      >
                        No lifecycle phases available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default LifecyclePhasesSection;
